const normalizeToken = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const athleteImagesContext = require.context("../images/athletes/athlets images", true, /\.(png|jpe?g|webp|gif)$/i);
const athleteImagePlaceholder = require("../images/athletes/portrait_placeholder.jpg");

const athleteLocalImages = athleteImagesContext
  .keys()
  .map((key) => {
    const fileName = key.split("/").pop() || "";
    const normalizedPath = normalizeToken(key);
    const normalizedName = normalizeToken(fileName);
    const kind = normalizedName.includes("action")
        ? "action"
        : normalizedName.includes("portrait") || normalizedPath.includes("halloffame")
          ? "portrait"
          : "";
    return {
      kind,
      normalizedName,
      normalizedPath,
      src: athleteImagesContext(key),
    };
  })
  .filter((img) => img.kind);

const matchesAthleteName = (fileNameNorm, firstNameNorm, lastNameNorm) => {
  if (!fileNameNorm || !firstNameNorm || !lastNameNorm) return false;
  if (fileNameNorm.includes(firstNameNorm) && fileNameNorm.includes(lastNameNorm)) return true;
  const firstShort = firstNameNorm.slice(0, Math.max(3, Math.min(5, firstNameNorm.length)));
  const lastShort = lastNameNorm.slice(0, Math.max(3, Math.min(5, lastNameNorm.length)));
  return fileNameNorm.includes(firstShort) && fileNameNorm.includes(lastShort);
};

const lastNameVariantsByAthlete = {
  felixhoffmann: ["hoffmann", "hoffman"],
  hektorkapustik: ["kapustik"],
  kirakapustikova: ["kapustikova"],
  laramalsiner: ["malsiner", "malisner"],
  jessicamalsiner: ["malsiner", "malisner"],
  janinahettichwalz: ["hettichwalz"],
  liabohme: ["bohme", "boehme"],
  luisagorlich: ["gorlich", "goerlich"],
  charlottegallbronner: ["gallbronner"],
};

const getLastNameVariants = (firstName, lastName) => {
  const fullNameNorm = `${normalizeToken(firstName)}${normalizeToken(lastName)}`;
  const base = normalizeToken(lastName);
  const extra = lastNameVariantsByAthlete[fullNameNorm] || [];
  return [...new Set([base, ...extra])];
};

const preferredImageNeedlesByAthlete = {
  annatwardosz: {
    portrait: ["portrait02", "portrait2"],
  },
  janinahettichwalz: {
    portrait: ["halloffame", "janinahettichwalz"],
  },
  florianliegl: {
    portrait: ["halloffame", "florianliegl"],
  },
  susanneriesch: {
    portrait: ["halloffame", "susanneriesch"],
  },
  martinazellner: {
    portrait: ["halloffame", "martinazellner"],
  },
};

const getLocalAthleteImage = (firstName, lastName, kind) => {
  const firstNameNorm = normalizeToken(firstName);
  const lastNameVariants = getLastNameVariants(firstName, lastName);
  const fullNameNorm = `${firstNameNorm}${normalizeToken(lastName)}`;
  const preferredNeedles = preferredImageNeedlesByAthlete[fullNameNorm]?.[kind] || [];
  const matches = athleteLocalImages.filter(
    (img) =>
      img.kind === kind &&
      lastNameVariants.some((lastNameNorm) => matchesAthleteName(img.normalizedName, firstNameNorm, lastNameNorm))
  );
  if (matches.length === 0) return { src: "", normalizedName: "" };

  const score = (img) => {
    let s = 0;
    if (lastNameVariants.some((ln) => img.normalizedName.includes(firstNameNorm) && img.normalizedName.includes(ln))) {
      s += 20;
    }
    const firstNameIndex = img.normalizedName.indexOf(firstNameNorm);
    const lastNameIndex = lastNameVariants.reduce((best, ln) => {
      const idx = img.normalizedName.indexOf(ln);
      return idx === -1 ? best : Math.min(best, idx);
    }, Number.POSITIVE_INFINITY);
    if (firstNameIndex >= 0 && lastNameIndex < Number.POSITIVE_INFINITY && firstNameIndex < lastNameIndex) {
      s += 15;
    }
    if (preferredNeedles.some((needle) => img.normalizedName.includes(needle))) s += 100;
    // Prefer explicit role tags (portrait/action) over generic hall-of-fame file names.
    if (kind === "portrait" && img.normalizedName.includes("portrait")) s += 5;
    if (kind === "action" && img.normalizedName.includes("action")) s += 5;
    // Slightly prefer filenames with more detail (e.g. portrait02 over portrait).
    s += Math.min(img.normalizedName.length, 30) * 0.01;
    return s;
  };

  matches.sort((a, b) => score(b) - score(a));
  if (matches[0]) return { src: matches[0].src, normalizedName: matches[0].normalizedName };
  return { src: "", normalizedName: "" };
};

const photoCreditsByAthlete = {
  "Anna Twardosz": {
    portrait: "skijumpingpl",
  },
  "Alma Siegismund": {
    action: "Kevin Voigt",
  },
  "Anne Häckel": {
    portrait: "VSC Klingenthal e.V/Florian Müller",
    action: "Jan Simon Schäfer",
  },
  "Josephine Pagnier": {
    portrait: "lucile.morat",
    action: "loganswney",
  },
  "Alina Nußbicker": {
    action: "shotsbyniklas / k_voigt_fotografie",
  },
  "Charlotte Gallbronner": {
    portrait: "k_voigt_fotografie",
    action: "k_voigt_fotografie",
  },
};

const athletes = [
  // Current Athletes
  {
    firstName: "Abigail",
    lastName: "Strate",
    achievements: [
      "Bronzemedaillengewinnerin Mixed-Mannschaft Olympia Peking",
      "2023/24 Gesamtweltcup Platz 11",
      "Gesamtweltcup 2025 Platz 15",
      "Gesamtweltcupfünfte 2026",
    ],
    sportDiscipline: "Skispringen",
    birthday: "22.02.2001",
    club: "Altius Nordic Ski Club (Calgary) ",
    interests: ["Kunst", "Design", "Fotografie"],
    profession: "Skispringerin, Graphic Design Student (Toronto Film School & Yorkville University)",
    hobby: "Ski fahren, Mountain Biking, Laufen, Surfen, Malen, Gitarre spielen, Imkerei",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Amandine",
    lastName: "Mengin",
    achievements: [
      "Vizeeuropameisterin 2025 Martell",
      "Juniorenweltmeisterin Staffel 2025 Östersund",
      "Zweifache Silbermedaillengewinnerin JWM 2025 Östersund",
    ],
    sportDiscipline: "Biathlon",
    birthday: "03.10.2004",
    club: "La Bressaude",
    interests: ["Ausgleichssportarten"],
    profession: "Schülerin/Studentin",
    hobby: "Wandern, Mountain-Biking, Unternehmungen mit Freunden",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "FRA",
  },
  {
    firstName: "Emily",
    lastName: "Teubner",
    achievements: ["Silbermedaille Juniorenweltmeisterschaft 2026 Team in Lillehammer"],
    sportDiscipline: "Skispringen",
    birthday: "03.11.2007",
    club: "SFV Rothenburg",
    interests: ["Wintersport allgemein"],
    profession: "Schülerin",
    hobby: "Kochen und Backen, Spaziergänge, Skifahren, Fahrrad fahren",
    portraitImageURL: "",
    actionImageURL: "",
    portraitDetailWrapperClass: "overflow-hidden",
    portraitDetailImageClass: "w-full h-[400px] object-cover object-[center_28%]",
    originCountryCode: "GER",
  },
  {
    firstName: "Charlotte",
    lastName: "Grandinger",
    achievements: [
      "Olympische Jugendwinterspiele 2024 Gangwon Silber Slalom",
      "Juniorenweltmeisterin Slalom Tarvisio 2025",
    ],
    sportDiscipline: "Ski Alpin",
    birthday: "14.02.2007",
    club: "Munich Youngsters",
    interests: ["Sport in der Natur", "Wintersport allgemein"],
    profession: "Schülerin/Studentin",
    hobby: "Skitouren gehen",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
  },
  {
    firstName: "Hektor",
    lastName: "Kapustik",
    achievements: ["Olympische Winterspiele 2026 Großschanze: 22. Platz"],
    sportDiscipline: "Skispringen",
    birthday: "23.04.2007",
    club: "KPSL SK",
    interests: ["Sport in der Natur", "Wintersport allgemein"],
    profession: "Schüler/Student",
    hobby: "Skitouren gehen, Skifahren, Musik",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "SVK",
  },
  {
    firstName: "Kira",
    lastName: "Kapustikova",
    achievements: ["Juniorenweltmeisterschaften 2026 Lillehammer 6. Platz"],
    sportDiscipline: "Skispringen",
    birthday: "13.04.2009",
    club: "KPSL SK",
    interests: ["Sport in der Natur", "Wintersport allgemein"],
    profession: "Schülerin/Studentin",
    hobby: "Wandern, Skitouren gehen, Skifahren, Musik, mit Freundinnen Unternehmungen machen",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "SVK",
  },
  {
    firstName: "Adrian",
    lastName: "Tittel",
    achievements: ["Junioren-Weltmeister 2023", "Bronzemedaillengewinner Junioren-WM 2025 Planica"],
    sportDiscipline: "Skispringen",
    birthday: "21.02.2004",
    club: "SG Nickelhütte Aue",
    interests: ["In Zukunft noch hoffentlich viele Sportliche Erfolge feiern zu dürfen"],
    profession: "Sportsoldat",
    hobby: "Zeit mit Familie und Freunden verbringen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Alina",
    lastName: "Nußbicker",
    achievements: [
      "Goldmedaillengewinnerin Junioren-WM Biathlon-Staffel in Estland 2024",
      "IBU Junior-Cup 2024/2025 mehrere Top-10-Platzierungen",
    ],
    sportDiscipline: "Biathlon",
    birthday: "14.10.2004",
    club: "SV Motor Tambach-Dietharz ",
    interests: ["Rennrad fahren", "verschiedene Sportarten (Bsp. Tour de france, Leichtathletik...)", "Mode"],
    profession: "Biathletin",
    hobby:
      "Zeit mit Familie und Freunde verbringen, Fernstudium in Sportökonomie, Biografien lesen, Konzerte besuchen,Backen ",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Anna-Fay",
    lastName: "Scharfenberg",
    achievements: [
      "Nachwuchs-Weltmeisterschaft Kanada im Team Platz 3",
      "Nachwuchs-Weltmeisterschaft Kanada im Einzel Platz 12",
      "Juniorenweltmeisterschaften 2026 Einzel 5. Platz",
      "Juniorenweltmeisterschaften 2026 Team Silber",
    ],
    sportDiscipline: "Skispringen",
    birthday: "14.04.2006",
    club: "SC Motor Zella-Mehlis",
    interests: ["alle anderen Sportarten", "Musik"],
    profession: "Zoll Ski Team",
    hobby: "Skifahren, Radfahren, Gitarre spielen ",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Cindy",
    lastName: "Haasch",
    achievements: [
      "Gesamtweltcup 2024/25 Platz 14",
      "Weltmeisterschaft Oberstdorf 2022 Platz 11 im Einzel",
      "Top-20-Platzierungen Saison 2024/25",
      "Erster Sommer Grand Prix Gesamtwertung Platz 21",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "19.04.2004",
    club: "TSG WSC 07 Ruhla",
    interests: ["Sport"],
    profession: "Berufssoldatin",
    hobby: "Lesen, häkeln",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "David",
    lastName: "Mach",
    achievements: ["Continental-Cup-Sieger 2022", "Gesamtweltcup 2024/25 Platz 17"],
    sportDiscipline: "Nordische Kombination",
    birthday: "14.05.2000",
    club: "TSV Buchenberg",
    interests: ["Sport"],
    profession: "Student Maschinenbau",
    hobby: "Wandern, Langlaufen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Emely",
    lastName: "Torazza",
    achievements: ["Zahlreiche Weltcupeinsätze", "Top-20-Platzierungen im Weltcup"],
    sportDiscipline: "Skispringen",
    birthday: "02.11.2004",
    club: "SC Oberstdorf",
    interests: ["Geschichte", "Musik"],
    profession: "Skispringerin",
    hobby: "Lesen, Zeit mit Familie, Klettern, Paddeltennis, Escaperooms",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Felix",
    lastName: "Hoffmann",
    achievements: [
      "Silber Juniorenweltmeisterschaft 2017 im Team",
      "Deutscher Juniorenmeister 2016 in Oberhof",
      "Vierfacher Continental-Cup-Sieger 2017,2023,2024",
      "Gesamtweltcup-Neunter 2025/2026",
    ],
    sportDiscipline: "Skispringen",
    birthday: "14.07.1997",
    club: "SWV Goldlauter-Heidersbach",
    interests: ["Sport", "Natur", "Technik"],
    profession: "Polizeivollzugsbeamter",
    hobby: "Zeit mit der Familie, Kochen, Wandern, Basteln",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Jenny",
    lastName: "Nowak",
    achievements: ["WM 2023 Silber Mixed Team", "Gesamtweltcup 2024/2025 Platz 8", "Juniorenweltmeisterin 2022"],
    sportDiscipline: "Nordische Kombination",
    birthday: "20.08.2002",
    club: "Skiclub Sohland 1928 e.V.",
    interests: ["Wintersport", "Radsport"],
    profession: "Sportsoldatin",
    hobby: "Zeit mit der Familie und Freunden verbringen, Rennrad fahren",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Josephine",
    lastName: "Pagnier",
    achievements: [
      "Gesamtweltcupranking 2022/23 Platz 16",
      "Zweifache Weltcupsiegerin 2023",
      "Gesamtweltcup 2025 Platz 23",
    ],
    sportDiscipline: "Skispringen",
    birthday: "04.06.2002",
    club: "Risoux-Club Chaux-Neuve",
    interests: ["Sport und Natur"],
    profession: "Skispringerin",
    hobby: "Wandern, Gleitschirmfliegen, in der Natur sein und mit ihr leben",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Lara",
    lastName: "Malsiner",
    achievements: [
      "Gesamtweltcup 2025 Platz 16",
      "Sommer Grand Prix Serie 2024 Gesamtsieg",
      "Saison 2024/25 mehrere Top-10-Platzierungen",
    ],
    sportDiscipline: "Track and Field",
    birthday: "14.04.2000",
    club: "Fiamme Gialle",
    interests: ["Sport allgemein"],
    profession: "Guardia di finanza",
    hobby: "Skifahren, Skibergsteigen, Wandern, Backen/Kochen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Jessica",
    lastName: "Malsiner",
    achievements: ["Italienische Meisterin 2022 in Villach", "Mehrere Top-10-Platzierungen im Continental Cup"],
    sportDiscipline: "Skispringen",
    birthday: "23.09.2002",
    club: "Gruppo sciatori Fiamme Gialle",
    interests: ["Sport"],
    profession: "Skispringerin",
    hobby: "Ich bin gerne draußen in der Natur. Sport Allgemein.",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Lia",
    lastName: "Böhme",
    achievements: ["Bronzemedaille Junioren-WM in Whistler 2023 Team"],
    sportDiscipline: "Spezialspringen",
    birthday: "16.07.2005",
    club: "VSC-Klingenthal",
    interests: ["Sport"],
    profession: "Schülerin",
    hobby: "Schwimmen, Volleyball",
    portraitImageURL: "",
    actionImageURL: "",
    borrowedActionImageFrom: { firstName: "Lara", lastName: "Malsiner" },
  },
  {
    firstName: "Luisa",
    lastName: "Görlich",
    achievements: ["WM 2023 Weltmeisterin Team", "Gesamtweltcup 2023 Platz 24"],
    sportDiscipline: "Skispringen",
    birthday: "21.12.1998",
    club: "WSV 08 Lauscha",
    interests: ["Psychologie", "mein Hund"],
    profession: "Skispringerin",
    hobby: "Kaffee trinken mit Freunden und Familie, Squash, an die frische Luft gehen",
    portraitImageURL: "",
    actionImageURL: "",
    borrowedActionImageFrom: { firstName: "Josephine", lastName: "Pagnier" },
  },
  {
    firstName: "Maria",
    lastName: "Gerboth",
    achievements: [
      "Trophee de la meilleure sauteuse 2024/25 beste Springerin der Saison",
      "Gesamtweltcup 2024/25 Platz 10",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "19.06.2002",
    club: "VFS Schmiedefeld",
    interests: ["Sport"],
    profession: "Nordische Kombiniererin",
    hobby: "Radfahren, Wandern",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Martin",
    lastName: "Hamann",
    achievements: [
      "Dreifacher Continental-Cup-Sieger im Einzel 2019 und 2020",
      "Gesamtweltcup 2020 Platz 23",
      "Amtierender deutscher Meister 2024",
    ],
    sportDiscipline: "Skispringen",
    birthday: "10.04.1997",
    club: "SG Nickelhütte Aue",
    interests: ["Fußball", "Organisieren/plann", "Landwirtschaft"],
    profession: "Bundespolizist (Obermeister)",
    hobby: "Skitour/fahren, SUP, Gravelbiken, Berge, Go Kart, Kochen, Barista",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Ronja",
    lastName: "Loh",
    achievements: [
      "Juniorenweltmeisterin Mixed-Team 2024",
      "Bronzemedaillengewinnerin JWM 2024",
      "Gesamtweltcup 2024/25 Platz 22",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "21.04.2005",
    club: "VSC Klingenthal",
    interests: ["Sport", "kreativ sein"],
    profession: "Sportsoldatin",
    hobby: "Klettern, Snowboardfahren, Zeit draußen mit Freunden verbringen",
    portraitImageURL: "",
    actionImageURL: "",
    borrowedActionImageFrom: { firstName: "Julina", lastName: "Kreibich" },
  },

  {
    firstName: "Simon",
    lastName: "Mach",
    achievements: ["Continental-Cup-Sieger 2024"],
    sportDiscipline: "Nordische Kombination",
    birthday: "20.10.2002",
    club: "TSV Buchenberg",
    interests: ["Sport"],
    profession: "Zollbeamter",
    hobby: "Wandern, Bergsteigen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Tristan",
    lastName: "Sommerfeldt",
    achievements: ["Dreifacher Juniorenweltmeister 2022, 2023, 2024 im Team"],
    sportDiscipline: "Nordische Kombination",
    birthday: "16.12.2004",
    club: "WSC Erzgebirge Oberwiesenthal",
    interests: ["Sport", "Kochen", "Reisen"],
    profession: "Sportsoldat",
    hobby: "Andere Sportarten wie Fußball, Radfahren, Golf oder Skialpin, Fotografie, mit Freunden treffen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Anne",
    lastName: "Häckel",
    achievements: ["Deutsche Jugendmeisterin Saison 2022/23", "Erster Sommer Grand Prix Gesamtwertung Platz 21"],
    sportDiscipline: "Nordische Kombination",
    birthday: "10.01.2005",
    club: "VSC Klingenthal",
    interests: ["Pflanzen", "Reisen"],
    profession: "Bundespolizistin",
    hobby: "Lesen, Wandern, Volleyball spielen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Robin",
    lastName: "Kloß",
    achievements: [
      "Continental Cup 2024 in Hinterzarten Platz 13",
      "FIS- Junioren-Weltmeisterschaften 2025 Platz 16 im Einzel und Platz 4 im Team",
      "Alpen Cup in Chaux-Neuve 2025 unteranderem Platz 4 und Platz 9",
    ],
    sportDiscipline: "Skispringen",
    birthday: "11.06.2005",
    club: "Ski-Club Willingen",
    interests: ["Fashion", "Fußball", "Sport allgemein"],
    profession: "Sportsoldat",
    hobby: "Sportliche Aktivitäten, Fußball spielen, Radfahren sowie Zeit mit Freunden verbringen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Anna",
    lastName: "Twardosz",
    achievements: [
      "Fünffache Polnische Meisterin 2016, 2018, 2023, 2024, 2025",
      "Sieg im Intercontinental Cup in Notodden 2024",
      "Olympiazehnte Einzelspringen Milano/Cortina 2026",
    ],
    sportDiscipline: "Skispringen",
    birthday: "12.04.2001",
    club: "AZS AWF Katowice",
    interests: ["Fotografie", "Fotoalben gestalten", "backen", "Sport"],
    profession: "Skispringerin",
    hobby:
      "Die Familie ist in meinem Leben sehr wichtig, deshalb versuche ich, so viel Zeit wie möglich mit ihr zu verbringen. Wenn ich auf Reisen bin, versuche ich immer, Sehenswürdigkeiten zu besuchen, um interessante Dinge oder Orte zu entdecken.",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Nicole",
    lastName: "Maurer",
    achievements: ["Top-21-Platzierung Nordische Ski-Weltmeisterschaften 2025", "Gesamtweltcup 2024/2025 Platz 28"],
    sportDiscipline: "",
    birthday: "28.11.2000",
    club: "Calgary",
    interests: ["Sport"],
    profession: "Studentin",
    hobby: "Wandern, Fischen, Jagen",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Charlotte",
    lastName: "Gallbronner",
    achievements: [
      "Junioren-Europameisterin im Single-Mixed 2025",
      "Junioren-Vizeweltmeisterin in der Staffel 2025",
      "Sieg im Einzelrennens beim IBU Junior Cups 2024/2025 in Ridnaun",
    ],
    sportDiscipline: "Biathlon",
    birthday: "25.09.2003",
    club: "",
    interests: ["Biathlon", "kochen", "backen", "Kaffee/Barista", "Handball"],
    profession: "Bundeswehrangestellte",
    hobby: "In meiner Freizeit unternehme ich gerne was mit Freunden und backe und koche gerne mal.",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Louis",
    lastName: "Obersteiner",
    achievements: ["Junioren WM Gold Team", "Olympische Jugendspiele Team Gold"],
    sportDiscipline: "",
    birthday: "10.08.2004",
    club: "Wiener Stadtadler und SC Les Contamines-Montjoie",
    interests: ["Golf", "Kitesurfen"],
    profession: " Student für Recht und Wirtschaft",
    hobby: "DJ(auflegen), Skifahren, Lesen, Volleyball",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Julina",
    lastName: "Kreibich",
    achievements: [
      "Junioren-Weltmeisterin im Team 2025",
      "FIS -Alpencup 2024/2025 Platz 3 im Einzel",
      "Mehrere Top-10-Platzierungen im Intercontinental Cup 2024/2025",
      "Juniorenweltmeisterschaften 2026 Einzel 14. Platz",
      "Juniorenweltmeisterschaften 2026 Team Silber",
    ],
    sportDiscipline: "Skispringen",
    birthday: "21.01.2008",
    club: "VSC Klingenthal",
    interests: ["Sport"],
    profession: "Zollbeamtin",
    hobby: "Lesen, Skifahren, Radfahren",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Richard",
    lastName: "Stenzel",
    achievements: [
      "Junioren-Weltmeisterschaften 2025 Bronzemedaille im Einzel, vierter Platz im Teamsprint und Gold im Mixed-Team",
      "Deutsche Meisterschaften 2023 und 2024 jeweils Vize-Meister Juniorenkategorie",
      "Acht Podestplätze im Alpencup",
      "Saison 2024/25 Weltcup in Lathi Platz 13",
      "Top10 Platzierung Weltcup 2025/2026",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "26.10.2005",
    club: "SC Motor Zella-Mehlis",
    interests: ["Sport"],
    profession: "Sportsoldat",
    hobby: "Fußball, Radfahren, Zeit mit Familie und Freunden",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Eric",
    lastName: "Frenzel",
    achievements: [
      "3facher Olympiasieger 2014,2018",
      "7facher Weltmeister 2011-2019",
      "18 WM-Medaillen 2011-2023",
      "5facher Gesamtweltcupsieger 2013-2017",
      "Erfolgreichster deutscher Skisportler aller Zeiten",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "21.11.1988",
    club: "SSV Geyer",
    interests: ["Alle Sportarten"],
    profession: "Bundestrainer Nordische Kombination, Student Wirtschaftsingenieurwesen",
    hobby: "Wandern, Mountainbiking, Ökologischer Landbau, Kochen",
    portraitImageURL: "",
    actionImageURL: "",
    portraitDetailWrapperClass: "overflow-hidden",
    portraitDetailImageClass: "w-full h-[400px] object-cover object-[center_28%]",
  },
  {
    firstName: "Karl",
    lastName: "Geiger",
    achievements: [
      "Zweiter Platz Gesamtweltcup 2021/22",
      "Sechsfacher Weltmeister",
      "Einmal Silber 2018 und zweimal Bronzemedaillengewinner 2022 bei Olympischen Spielen",
      "Bronzemedaillengewinner Skiweltmeisterschaften und Gold im Mixed-Team 2023",
    ],
    sportDiscipline: "Skispringen",
    birthday: "11.02.1993",
    club: "SC Oberstdorf",
    interests: ["Natur", "Technik"],
    profession: "Bachelor Energie/Umwelttechnik",
    hobby: "Skifahren, Wandern, mit der Familie reisen, Pate des Allgäuer Bergwalds",
    portraitImageURL: "",
    actionImageURL: "",
  },
  {
    firstName: "Janina",
    lastName: "Hettich-Walz",
    achievements: ["Vizeweltmeisterin Staffel 2021", "Zweifache Europameisterin 2022", "Vizeweltmeisterin Einzel 2024"],
    sportDiscipline: "Biathlon",
    birthday: "16.06.1996",
    club: "SC Schönwald",
    interests: ["Imkerei", "Garten und Natur"],
    profession: "Sportsoldatin",
    hobby: "Skifahren, Wandern, Mountainbiking, mit der Familie reisen und Unternehmungen machen",
    portraitImageURL: "",
    actionImageURL: "",
  },

  // Former Athletes
  {
    firstName: "Martina",
    lastName: "Ertl",
    achievements: [
      "Weltmeisterin 2001,2005",
      "Zweifache Silbermedaillengewinnerin Olympische Spiele 1994 und 1998",
      "Zweifache Bronzemedaillengewinnerin WM 1993 und 1996",
    ],
    sportDiscipline: "Ski Alpin",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Skirennläuferin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Eric",
    lastName: "Frenzel",
    achievements: [
      "3facher Olympiasieger 2014,2018",
      "7facher Weltmeister 2011-2019",
      "18 WM-Medaillen 2011-2023",
      "5facher Gesamtweltcupsieger 2013-2017",
      "Erfolgreichster deutscher Skisportler aller Zeiten",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "21.11.1988",
    club: "SSV Geyer",
    interests: [
      "Familie",
      "Technik",
      "Uhren",
      "Handwerk",
      "Landwirtschaft",
      "Wandern",
      "Unterstützung des Projekts 'Eric-Frenzel -Trails'",
    ],
    profession: "Bundestrainer Nordische Kombination",
    hobby: "Naturerleben, Reisen, Ausgleichssportarten, Wandern, Nordic Walking, Trailrunning",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Florian",
    lastName: "Liegl",
    achievements: ["Zweifacher Juniorenweltmeister", "Gesamtweltcupfünfter 2002/2003"],
    sportDiscipline: "Skispringen",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Sportler",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "AUT",
    isFormer: true,
  },
  {
    firstName: "Janina",
    lastName: "Hettich-Walz",
    achievements: ["Vizeweltmeisterin Staffel 2021", "Zweifache Europameisterin 2022", "Vizeweltmeisterin Einzel 2024"],
    sportDiscipline: "Biathlon",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Biathletin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Karl",
    lastName: "Geiger",
    achievements: [
      "Zweiter Platz Gesamtweltcup 2021/22",
      "Sechsfacher Weltmeister",
      "Einmal Silber 2018 und zweimal Bronzemedaillengewinner 2022 bei Olympischen Spielen",
      "Bronzemedaillengewinner Skiweltmeisterschaften und Gold im Mixed-Team 2023",
    ],
    sportDiscipline: "Skispringen",
    birthday: "11.02.1993",
    club: "Skiclub Oberstdorf",
    interests: ["Sport", "Technik", "Handwerk"],
    profession: "Zollbeamter",
    hobby: "Unternehmungen aller Art mit Freunden und Familie",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Nico",
    lastName: "Hülkenberg",
    achievements: [
      "GP2-Meister 2009",
      "Formel-1-Debüt 2010 bei Williams",
      "Pole Position beim Brasilien-GP 2010",
      "Dritter Platz in der Formel 1 in Silverstone 2023 (erstes F1-Podium)",
      "24h-Le-Mans-Sieger 2015 mit Porsche",
    ],
    sportDiscipline: "Formel 1",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Rennfahrer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Bernd",
    lastName: "Schneider",
    achievements: [
      "Vizeweltmeister 2002",
      "WM-Dritter 2006",
      "81 Länderspiele für Deutschland",
      "Vize-Europameister (EM 2008)",
      "Zweifacher DFB-Pokalsieger mit Bayer 04 Leverkusen",
      "Deutscher Vizemeister 2000 und 2002",
    ],
    sportDiscipline: "Fußball",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Fußballer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Stefanie",
    lastName: "Böhler",
    achievements: [
      "Olympische Silbermedaille 2014",
      "Zweifache WM-Silbermedaillengewinnerin (Staffel)",
      "Über 15 Jahre Mitglied der deutschen Langlauf-Nationalmannschaft",
    ],
    sportDiscipline: "Skilanglauf",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Langläuferin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Andrea",
    lastName: "Henkel",
    achievements: [
      "Vier olympische Medaillen (2 Gold)",
      "16 WM-Medaillen (8 Gold)",
      "Gesamtweltcupsiegerin 2006/07",
      "46 Weltcupsiege",
      "Erste Biathletin mit WM-Gold in allen Einzeldisziplinen",
    ],
    sportDiscipline: "Biathlon",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Biathletin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Anja",
    lastName: "Pärson",
    achievements: [
      "Sechs olympische Medaillen (1 Gold, 1 Silber, 4 Bronze)",
      "13 WM-Medaillen (7 Gold)",
      "Gesamtweltcupsiegerin 2003/04 und 2004/05",
      "42 Weltcupsiege",
      "Eine der wenigen Fahrerinnen mit Siegen in allen alpinen Disziplinen",
    ],
    sportDiscipline: "Ski Alpin",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Skirennläuferin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "SWE",
    isFormer: true,
  },
  {
    firstName: "Björn",
    lastName: "Kircheisen",
    achievements: [
      "Zweifacher olympischer Silbermedaillengewinner",
      "WM-Gold 2007",
      "Vier WM-Silbermedaillen",
      "Zweifacher WM-Bronzemedaillengewinner",
      "Mehrfacher Team-Erfolg im Weltcup mit Deutschland",
    ],
    sportDiscipline: "Nordische Kombination",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Nordischer Kombinierer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Julia",
    lastName: "Görges",
    achievements: [
      "Grand-Slam-Titel im Doppel (Wimbledon 2017)",
      "Halbfinale Wimbledon im Einzel (2018)",
      "WTA Finals Halbfinale (2017)",
      "Top 9 der Weltrangliste (2018)",
    ],
    sportDiscipline: "Tennis",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Tennisspielerin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Kirsten",
    lastName: "Flipkens",
    achievements: [
      "Grand-Slam-Halbfinale im Einzel (Wimbledon 2013)",
      "Grand-Slam-Titel im Mixed-Doppel (US Open 2022)",
      "WTA-Einzeltitel (Quebec 2012)",
      "Platz 13 Weltrangliste (2013)",
    ],
    sportDiscipline: "Tennis",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Tennisspielerin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "BEL",
    isFormer: true,
  },
  {
    firstName: "Lars",
    lastName: "Bender",
    achievements: [
      "Olympische Silbermedaille 2016 in Rio mit der deutschen Nationalmannschaft",
      "Deutscher Pokalsieger 2014/15 mit Bayer 04 Leverkusen",
      "Vize-Weltmeister bei der U17-EM 2007",
      "Mehr als 250 Bundesligaspiele",
      "Über 20 Länderspiele für Deutschland",
      "Langjähriger Kapitän bei Bayer Leverkusen",
    ],
    sportDiscipline: "Fußball",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Fußballer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Lucie",
    lastName: "Safarova",
    achievements: [
      "Grand-Slam-Titel im Doppel (French Open 2015)",
      "Grand-Slam-Titel im Doppel (Australian Open 2015)",
      "Grand-Slam-Finale im Einzel (French Open 2015)",
      "Fünffache Grand-Slam-Titelträgerin im Doppel",
      "Platz 5 Weltrangliste (2015)",
    ],
    sportDiscipline: "Tennis",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Tennisspielerin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "CZE",
    isFormer: true,
  },
  {
    firstName: "Magdalena",
    lastName: "Neuner",
    achievements: [
      "Zweifache olympische Goldmedaillengewinnerin (2006 und 2010)",
      "Zwölffache Weltmeisterin",
      "34 Weltcupsiege",
      "Dreifache Gesamtweltcupsiegerin (2007/08, 2008/09, 2009/10)",
      "Olympische Silber- und Bronzemedaille",
    ],
    sportDiscipline: "Biathlon",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Biathletin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Mandy",
    lastName: "Minella",
    achievements: [
      "Zweifache WTA-Doppeltitelgewinnerin",
      "Platz 66 Weltrangliste (2012)",
      "Langjährige Nummer 1 von Luxemburg im Damentennis",
      "Mehrfache Teilnahme an Grand-Slam-Turnieren im Einzel und Doppel",
    ],
    sportDiscipline: "Tennis",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Tennisspielerin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "LUX",
    isFormer: true,
  },
  {
    firstName: "Martina",
    lastName: "Glagow",
    achievements: [
      "Zweifache olympische Silbermedaillengewinnerin (2006)",
      "Dreifache Weltmeisterin (insgesamt 9 WM-Medaillen)",
      "Gesamtweltcup-Siegerin im Verfolgungsweltcup (2005/06)",
      "10 Weltcupsiege im Biathlon",
    ],
    sportDiscipline: "Biathlon",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Biathletin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Martina",
    lastName: "Zellner",
    achievements: [
      "Sprintweltmeisterin 1999",
      "Zweifache olympische Silbermedaillengewinnerin",
      "Weltmeisterin in der Staffel",
      "Mehrere WM- und Weltcup-Podestplätze",
      "Teil der erfolgreichen deutschen Biathlon-Staffel in den 1990er Jahren",
    ],
    sportDiscipline: "Biathlon",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Biathletin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Susanne",
    lastName: "Riesch",
    achievements: ["2009/2010 Platz 7 Slalomweltcup"],
    sportDiscipline: "Ski Alpin",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Skirennläuferin",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Red Bull",
    lastName: "Toro Rosso",
    achievements: [
      "Formel-1-Team 2006 bis 2019",
      "Sieg beim Italien-GP 2008",
      "Schwesterteam von Red Bull Racing mit Fokus auf Junioren- und Talente-Entwicklung",
    ],
    sportDiscipline: "Formel 1",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Rennstall",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "AUT",
    isFormer: true,
  },
  {
    firstName: "Sven",
    lastName: "Bender",
    achievements: [
      "Olympische Silbermedaille 2016 in Rio mit der deutschen Nationalmannschaft",
      "Deutscher Meister 2011/12 mit Borussia Dortmund",
      "Zweifacher Deutscher Meister 2011 und 2012",
      "DFB-Pokalsieger 2016/17 mit Borussia Dortmund",
      "Über 300 Bundesliga-Spiele",
    ],
    sportDiscipline: "Fußball",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Fußballer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "GER",
    isFormer: true,
  },
  {
    firstName: "Sven-Göran",
    lastName: "Eriksson",
    achievements: [
      "Trainer der englischen Nationalmannschaft von 2001 bis 2006",
      "Zweimal Viertelfinale bei Weltmeisterschaften mit England (2002 und 2006)",
      "Trainer von Top-Klubs wie SS Lazio und Manchester City",
      "Serie-A-Meister 1999/2000 mit Lazio Rom",
      "Gewinner des Europapokals der Pokalsieger 1998/99 mit Lazio",
    ],
    sportDiscipline: "Fußball",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Fußballtrainer",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "SWE",
    isFormer: true,
  },
  {
    firstName: "Stefan",
    lastName: "Edberg",
    achievements: [
      "Gewinner Wimbledon 1988,1990",
      "Gewinner US Open 1991,1992",
      "Gewinner Australian Open 1985,1987",
      "41 Einzeltitel",
      "Stefan Edberg Tennis Academy im Cascade Resort, Lagos, Algarve, Portugal",
      "Fokus auf Nachwuchsförderung im Tennis für Kinder und Jugendliche",
      "Verbindung von Techniktraining, Athletik und mentaler Entwicklung",
      "Zusammenarbeit mit lokalen Tennisvereinen und nationalen Verbänden",
    ],
    sportDiscipline: "Tennis",
    birthday: "",
    club: "",
    interests: ["Sport"],
    profession: "Tennisakademie",
    hobby: "",
    portraitImageURL: "",
    actionImageURL: "",
    originCountryCode: "SWE",
    isFormer: true,
  },
];

// Translation mapping for all terms
const translations = {
  de: {
    sportDisciplines: {
      "Skispringen": "Skispringen",
      "Biathlon": "Biathlon",
      "Nordische Kombination": "Nordische Kombination",
      "Ski Alpin": "Ski Alpin",
      "Spezialspringen": "Spezialspringen",
      "Track and Field": "Leichtathletik",
      "Tennis": "Tennis",
      "Fußball": "Fußball",
      "Formel 1": "Formel 1",
      "DTM": "DTM",
      "Langlauf": "Langlauf",
      "Skilanglauf": "Skilanglauf",
    },
    professions: {
      "Skispringerin": "Skispringerin",
      "Skispringer": "Skispringer",
      "Biathletin": "Biathletin",
      "Nordische Kombiniererin": "Nordische Kombiniererin",
      "Nordischer Kombinierer": "Nordischer Kombinierer",
      "Skirennläuferin": "Skirennläuferin",
      "Sportsoldatin": "Sportsoldatin",
      "Sportsoldat": "Sportsoldat",
      "Bundespolizistin": "Bundespolizistin",
      "Polizeivollzugsbeamter": "Polizeivollzugsbeamter",
      "Polizist": "Polizist",
      "Polizeimeisterin": "Polizeimeisterin",
      "Zollbeamter": "Zollbeamter",
      "Schülerin": "Schülerin",
      "Schülerin/Studentin": "Schülerin/Studentin",
      "Berufssoldatin": "Berufssoldatin",
      "Bundestrainer Nordische Kombination": "Bundestrainer Nordische Kombination",
      "Bundeswehrangestellte": "Bundeswehrangestellte",
      "Tennisspielerin": "Tennisspielerin",
      "Fußballer": "Fußballer",
      "Fußballtrainer": "Fußballtrainer",
      "Rennstall": "Rennstall",
      "Tennisakademie": "Tennisakademie",
      "Sportler": "Sportler",
      " Student für Recht und Wirtschaft": " Student für Recht und Wirtschaft",
      "Rennfahrer": "Rennfahrer",
      "Langläuferin": "Langläuferin",
    },
    achievements: {},
    interests: {},
    hobbies: {},
  },
  en: {
    sportDisciplines: {
      "Skispringen": "Ski Jumping",
      "Biathlon": "Biathlon",
      "Nordische Kombination": "Nordic Combined",
      "Ski Alpin": "Alpine Skiing",
      "Spezialspringen": "Special Jumping",
      "Track and Field": "Track and Field",
      "Tennis": "Tennis",
      "Fußball": "Football",
      "Formel 1": "Formula 1",
      "DTM": "DTM",
      "Langlauf": "Cross-Country Skiing",
      "Skilanglauf": "Cross-Country Skiing",
    },
    professions: {
      "Skispringerin": "Ski Jumper",
      "Skispringer": "Ski Jumper",
      "Biathletin": "Biathlete",
      "Nordische Kombiniererin": "Nordic Combined Athlete",
      "Nordischer Kombinierer": "Nordic Combined Athlete",
      "Skirennläuferin": "Alpine Skier",
      "Sportsoldatin": "Professional Soldier",
      "Sportsoldat": "Professional Soldier",
      "Bundespolizistin": "Federal Police Officer",
      "Polizeivollzugsbeamter": "Police Officer",
      "Polizist": "Police Officer",
      "Polizeimeisterin": "Police Sergeant",
      "Zollbeamter": "Customs Officer",
      "Schülerin": "Student",
      "Schülerin/Studentin": "Student",
      "Berufssoldatin": "Professional Soldier",
      "Bundestrainer Nordische Kombination": "National Coach Nordic Combined",
      "Bundeswehrangestellte": "Federal Armed Forces Employee",
      "Tennisspielerin": "Tennis Player",
      "Fußballer": "Football Player",
      "Fußballtrainer": "Football Coach",
      "Rennstall": "Racing Team",
      "Tennisakademie": "Tennis Academy",
      "Sportler": "Athlete",
      " Student für Recht und Wirtschaft": " Student of Law and Economics",
      "Rennfahrer": "Racing Driver",
      "Langläuferin": "Cross-Country Skier",
      "Zoll Ski Team": "Customs Ski Team",
      "Sportsoldat Sportfördergruppe der Bundeswehr Sonthofen":
        "Professional Soldier, Bundeswehr Sports Promotion Group Sonthofen",
      "Bundespolizist (Obermeister)": "Federal Police Officer (Senior Sergeant)",
      "Studentin": "Student",
      "Student Maschinenbau": "Mechanical engineering student",
      "Zollbeamtin": "Customs Officer",
      "Student Wirtschaftsingenieurwesen": "Industrial engineering student",
      "Bachelor Energie/Umwelttechnik": "BSc Energy/Environmental Engineering",
      "Bundestrainer Nordische Kombination, Student Wirtschaftsingenieurwesen":
        "National Coach Nordic Combined, Industrial engineering student",
    },
    achievements: {
      "Bronzemedaillengewinnerin Mixed-Mannschaft Olympia Peking": "Bronze medal winner Mixed Team Olympics Beijing",
      "2023/24 Gesamtweltcup Platz 11": "2023/24 Overall World Cup 11th place",
      "Gesamtweltcup 2025 Platz 15": "2025 Overall World Cup 15th place",
      "Gesamtweltcupfünfte 2026": "5th place in the overall World Cup 2026",
      "Olympiazehnte Einzelspringen Milano/Cortina 2026": "10th place Olympic individual ski jumping Milano/Cortina 2026",
      "Juniorenweltmeisterschaften 2026 Einzel 5. Platz": "Junior World Championships 2026 individual 5th place",
      "Juniorenweltmeisterschaften 2026 Team Silber": "Junior World Championships 2026 team silver",
      "Gesamtweltcup-Neunter 2025/2026": "9th place in the overall World Cup 2025/2026",
      "Juniorenweltmeisterschaften 2026 Einzel 14. Platz": "Junior World Championships 2026 individual 14th place",
      "Junioren-Weltmeister 2023": "Junior World Champion 2023",
      "Bronzemedaillengewinner Junioren-WM 2025 Planica": "Bronze medal winner Junior World Championships 2025 Planica",
      "Goldmedaillengewinnerin Junioren-WM Biathlon-Staffel in Estland 2024": "Gold medal winner Junior World Championships Biathlon Relay in Estonia 2024",
      "IBU Junior-Cup 2024/2025 mehrere Top-10-Platzierungen": "IBU Junior Cup 2024/2025 multiple top-10 placements",
      "Zweifache Goldmedaillengewinnerin Juniorenweltmeisterschaft 2024": "Two-time gold medal winner Junior World Championships 2024",
      "Junioren-Weltmeisterschaften 2025 Silbermedaille mit der Frauenstaffel": "Junior World Championships 2025 silver medal with women's relay",
      "WM 2021 Weltmeisterin Mixed Team": "2021 World Champion Mixed Team",
      "WM 2023 Weltmeisterin Team": "2023 World Champion Team",
      "Gesamtweltcup 2023 Platz 18": "2023 Overall World Cup 18th place",
      "Nachwuchs-Weltmeisterschaft Kanada im Team Platz 3": "Youth World Championships Canada team 3rd place",
      "Nachwuchs-Weltmeisterschaft Kanada im Einzel Platz 12": "Youth World Championships Canada individual 12th place",
      "Gesamtweltcup 2024/25 Platz 14": "2024/25 Overall World Cup 14th place",
      "Weltmeisterschaft Oberstdorf 2022 Platz 11 im Einzel": "World Championships Oberstdorf 2022 11th place individual",
      "Top-20-Platzierungen Saison 2024/25": "Top-20 placements 2024/25 season",
      "Erster Sommer Grand Prix Gesamtwertung Platz 21": "First Summer Grand Prix overall ranking 21st place",
      "Continental-Cup-Sieger 2022": "Continental Cup winner 2022",
      "Gesamtweltcup 2024/25 Platz 17": "2024/25 Overall World Cup 17th place",
      "Junioren WM Gold Team": "Junior World Championships team gold",
      "Olympische Jugendspiele Team Gold": "Youth Olympic Games team gold",
      "Top10 Platzierung Weltcup 2025/2026": "Top 10 finish in the World Cup 2025/2026",
      "IBU-Gesamtwertung Platz 5 2024 / in der Einzelwertung den zweiten Platz": "IBU overall ranking 5th place 2024 / 2nd place in individual ranking",
      "Charlotte Gallbronner": "Charlotte Gallbronner",
      "Junioren-Europameisterin im Single-Mixed 2025": "Junior European Champion Single-Mixed 2025",
      "Junioren-Vizeweltmeisterin in der Staffel 2025": "Junior Vice World Champion in relay 2025",
      "Sieg im Einzelrennens beim IBU Junior Cups 2024/2025 in Ridnaun": "Victory in individual race at IBU Junior Cups 2024/2025 in Ridnaun",
      "Zahlreiche Weltcupeinsätze": "Numerous World Cup appearances",
      "Top-20-Platzierungen im Weltcup": "Top-20 placements in World Cup",
      "3facher Olympiasieger 2014,2018": "3-time Olympic champion 2014, 2018",
      "7facher Weltmeister 2011-2019": "7-time world champion 2011-2019",
      "18 WM-Medaillen 2011-2023": "18 World Championship medals 2011-2023",
      "5facher Gesamtweltcupsieger 2013-2017": "5-time overall World Cup winner 2013-2017",
      "Silber Juniorenweltmeisterschaft 2017 im Team": "Silver Junior World Championships 2017 team",
      "Deutscher Juniorenmeister 2016 in Oberhof": "German Junior Champion 2016 in Oberhof",
      "Vierfacher Continental-Cup-Sieger 2017,2023,2024": "Four-time Continental Cup winner 2017, 2023, 2024",
      "Vizeweltmeisterin Staffel 2021": "Vice World Champion relay 2021",
      "Zweifache Europameisterin 2022": "Two-time European Champion 2022",
      "Vizeweltmeisterin Einzel 2024": "Vice World Champion individual 2024",
      "WM 2023 Silber Mixed Team": "2023 World Championships silver Mixed Team",
      "Gesamtweltcup 2024/2025 Platz 8": "2024/2025 Overall World Cup 8th place",
      "Juniorenweltmeisterin 2022": "Junior World Champion 2022",
      "Gesamtweltcupranking 2022/23 Platz 16": "2022/23 Overall World Cup ranking 16th place",
      "Zweifache Weltcupsiegerin 2023": "Two-time World Cup winner 2023",
      "Gesamtweltcup 2025 Platz 23": "2025 Overall World Cup 23rd place",
      "Silbermedaillengewinner Olympische Spiele 2022 im Team": "Silver medal winner Olympic Games 2022 team",
      "Zweifacher Junioren-Weltmeister 2019 in Lathi": "Two-time Junior World Champion 2019 in Lahti",
      "Dreifacher Weltcupsieger 2022 und 2023": "Three-time World Cup winner 2022 and 2023",
      "Gesamtweltcup 2024/25 Platz 4": "2024/25 Overall World Cup 4th place",
      "Skisprung-Junioren-Weltmeister 2023 in Whistler": "Ski Jumping Junior World Champion 2023 in Whistler",
      "Sieger der Nordischen Skispiele der OPA 2020 im Team und Einzel": "Winner of Nordic Ski Games OPA 2020 team and individual",
      "Heimweltcup 2021/22 in Titisee-Neustadt Platz 11 und Platz 13 im Einzel": "Home World Cup 2021/22 in Titisee-Neustadt 11th and 13th place individual",
      "Zweiter Platz Gesamtweltcup 2021/22": "2nd place Overall World Cup 2021/22",
      "Sechsfacher Weltmeister": "Six-time world champion",
      "Einmal Silber 2018 und zweimal Bronzemedaillengewinner 2022 bei Olympischen Spielen": "One silver 2018 and two-time bronze medal winner 2022 at Olympic Games",
      "Bronzemedaillengewinner Skiweltmeisterschaften und Gold im Mixed-Team 2023": "Bronze medal winner Ski World Championships and gold in Mixed Team 2023",
      "Gesamtweltcup 2025 Platz 16": "2025 Overall World Cup 16th place",
      "Sommer Grand Prix Serie 2024 Gesamtsieg": "Summer Grand Prix Series 2024 overall victory",
      "Saison 2024/25 mehrere Top-10-Platzierungen": "2024/25 season multiple top-10 placements",
      "Italienische Meisterin 2022 in Villach": "Italian Champion 2022 in Villach",
      "Mehrere Top-10-Platzierungen im Continental Cup": "Multiple top-10 placements in Continental Cup",
      "Bronzemedaille Junioren-WM in Whistler 2023 Team": "Bronze medal Junior World Championships in Whistler 2023 team",
      "Gesamtweltcup 2023 Platz 24": "2023 Overall World Cup 24th place",
      "Trophee de la meilleure sauteuse 2024/25 beste Springerin der Saison": "Trophy for best jumper 2024/25 best jumper of the season",
      "Gesamtweltcup 2024/25 Platz 10": "2024/25 Overall World Cup 10th place",
      "Dreifacher Continental-Cup-Sieger im Einzel 2019 und 2020": "Three-time Continental Cup winner individual 2019 and 2020",
      "Gesamtweltcup 2020 Platz 23": "2020 Overall World Cup 23rd place",
      "Amtierender deutscher Meister 2024": "Current German champion 2024",
      "Weltmeisterin 2001,2005": "World champion 2001, 2005",
      "Zweifache Silbermedaillengewinnerin Olympische Spiele 1994 und 1998": "Two-time silver medal winner Olympic Games 1994 and 1998",
      "Zweifache Bronzemedaillengewinnerin WM 1993 und 1996": "Two-time bronze medal winner World Championships 1993 and 1996",
      "Juniorenweltmeisterin Mixed-Team 2024": "Junior World Champion Mixed Team 2024",
      "Bronzemedaillengewinnerin JWM 2024": "Bronze medal winner Junior World Championships 2024",
      "Gesamtweltcup 2024/25 Platz 22": "2024/25 Overall World Cup 22nd place",
      "Nord Cup 2022 Siegerin der ersten Station": "Nord Cup 2022 winner of first station",
      "Platz 2 im Spezialspringen Schülerklasse S14/15": "2nd place in special jumping student class S14/15",
      "Deutsche Jugendmeisterin 2024 Einzel": "German Youth Champion 2024 individual",
      "Deutsche Jugendmeisterin 2024 Team": "German Youth Champion 2024 team",
      "Continental-Cup-Sieger 2024": "Continental Cup winner 2024",
      "Dreifacher Juniorenweltmeister 2022, 2023, 2024 im Team": "Three-time Junior World Champion 2022, 2023, 2024 team",
      "Zweifacher Continental-Cup-Sieger 2023": "Two-time Continental Cup winner 2023",
      "Zweifacher Continental-Cup-Sieger Team 2023": "Two-time Continental Cup winner team 2023",
      "Gesamtweltcup 2024/25 Platz 11": "2024/25 Overall World Cup 11th place",
      "Deutsche Jugendmeisterin Saison 2022/23": "German Youth Champion 2022/23 season",
      "Gesamtwertung IBU-Cup 2023/2024 Platz 9": "IBU Cup overall ranking 2023/2024 9th place",
      "Europameisterin 2023 in Lenzerheide im Einzel und Silbermedaillengewinnerin Mixed-Staffel": "European Champion 2023 in Lenzerheide individual and silver medal winner Mixed Relay",
      "Continental Cup 2024 in Hinterzarten Platz 13": "Continental Cup 2024 in Hinterzarten 13th place",
      "FIS- Junioren-Weltmeisterschaften 2025 Platz 16 im Einzel und Platz 4 im Team": "FIS Junior World Championships 2025 16th place individual and 4th place team",
      "Alpen Cup in Chaux-Neuve 2025 unteranderem Platz 4 und Platz 9": "Alpen Cup in Chaux-Neuve 2025 including 4th and 9th place",
      "Vier FIS-Rennen aufs Podium 2023/24": "Four FIS races on podium 2023/24",
      "Nordische Kombination Herren 9 Mandate": "Nordic Combined men 9 mandates",
      "Bronzemedaille beim European Youth Olympic Festival im Team 2023": "Bronze medal at European Youth Olympic Festival team 2023",
      "Junioren-Vizeweltmeister 2024 in Planica": "Junior Vice World Champion 2024 in Planica",
      "Fünffache Polnische Meisterin 2016, 2018, 2023, 2024, 2025": "Five-time Polish Champion 2016, 2018, 2023, 2024, 2025",
      "Sieg im Intercontinental Cup in Notodden 2024": "Victory in Intercontinental Cup in Notodden 2024",
      "Junioren-Weltmeisterin im Team 2025": "Junior World Champion team 2025",
      "FIS -Alpencup 2024/2025 Platz 3 im Einzel": "FIS Alpencup 2024/2025 3rd place individual",
      "Mehrere Top-10-Platzierungen im Intercontinental Cup 2024/2025": "Multiple top-10 placements in Intercontinental Cup 2024/2025",
      "Junioren-Weltmeisterschaften 2025 Bronzemedaille im Einzel, vierter Platz im Teamsprint und Gold im Mixed-Team": "Junior World Championships 2025 bronze medal individual, fourth place in team sprint and gold in Mixed Team",
      "Deutsche Meisterschaften 2023 und 2024 jeweils Vize-Meister Juniorenkategorie": "German Championships 2023 and 2024 each vice-champion junior category",
      "Acht Podestplätze im Alpencup": "Eight podium places in Alpencup",
      "Saison 2024/25 Weltcup in Lathi Platz 13": "2024/25 season World Cup in Lahti 13th place",
      "Bronze bei den Olympischen Jugendspielen 2024": "Bronze at Youth Olympic Games 2024",
      "Drei Siege in FIS-Rennen": "Three victories in FIS races",
      "12 FIS-Podien": "12 FIS podiums",
      "Japanischer Meister 2023 in Sapporo": "Japanese Champion 2023 in Sapporo",
      "Skispringen Damen  12 Mandate": "Ski Jumping women 12 mandates",
      "Top-21-Platzierung Nordische Ski-Weltmeisterschaften 2025": "Top-21 placement Nordic Ski World Championships 2025",
      "Gesamtweltcup 2024/2025 Platz 28": "2024/2025 Overall World Cup 28th place",
      "GP2-Meister 2009": "GP2 Champion 2009",
      "Formel-1-Debüt 2010 bei Williams": "Formula 1 debut in 2010 with Williams",
      "Pole Position beim Brasilien-GP 2010": "Pole position at the 2010 Brazilian Grand Prix",
      "Dritter Platz in der Formel 1 in Silverstone 2023 (erstes F1-Podium)":
        "Third place in Formula 1 at Silverstone 2023 (first F1 podium)",
      "24h-Le-Mans-Sieger 2015 mit Porsche": "24 Hours of Le Mans winner in 2015 with Porsche",
      "Vizeweltmeister 2002": "World Championship runner-up in 2002",
      "WM-Dritter 2006": "Third place at the 2006 World Cup",
      "81 Länderspiele für Deutschland": "81 international caps for Germany",
      "Vize-Europameister (EM 2008)": "European Championship runner-up (2008)",
      "Zweifacher DFB-Pokalsieger mit Bayer 04 Leverkusen": "Two-time DFB Cup winner with Bayer 04 Leverkusen",
      "Deutscher Vizemeister 2000 und 2002": "German Bundesliga runner-up in 2000 and 2002",
      "Olympische Silbermedaille 2014": "Olympic silver medal in 2014",
      "Zweifache WM-Silbermedaillengewinnerin (Staffel)": "Two-time World Championship silver medalist (relay)",
      "Über 15 Jahre Mitglied der deutschen Langlauf-Nationalmannschaft":
        "Member of the German cross-country skiing national team for over 15 years",
      "Vier olympische Medaillen (2 Gold)": "Four Olympic medals (2 gold)",
      "16 WM-Medaillen (8 Gold)": "16 World Championship medals (8 gold)",
      "Gesamtweltcupsiegerin 2006/07": "Overall World Cup winner 2006/07",
      "46 Weltcupsiege": "46 World Cup victories",
      "Erste Biathletin mit WM-Gold in allen Einzeldisziplinen":
        "First biathlete to win World Championship gold in all individual disciplines",
      "Sechs olympische Medaillen (1 Gold, 1 Silber, 4 Bronze)":
        "Six Olympic medals (1 gold, 1 silver, 4 bronze)",
      "13 WM-Medaillen (7 Gold)": "13 World Championship medals (7 gold)",
      "Gesamtweltcupsiegerin 2003/04 und 2004/05": "Overall World Cup winner 2003/04 and 2004/05",
      "42 Weltcupsiege": "42 World Cup victories",
      "Eine der wenigen Fahrerinnen mit Siegen in allen alpinen Disziplinen":
        "One of the few skiers with wins in all alpine disciplines",
      "Zweifacher olympischer Silbermedaillengewinner": "Two-time Olympic silver medal winner",
      "WM-Gold 2007": "World Championship gold in 2007",
      "Vier WM-Silbermedaillen": "Four World Championship silver medals",
      "Zweifacher WM-Bronzemedaillengewinner": "Two-time World Championship bronze medal winner",
      "Mehrfacher Team-Erfolg im Weltcup mit Deutschland":
        "Multiple World Cup team successes with Germany",
      "Grand-Slam-Titel im Doppel (Wimbledon 2017)": "Grand Slam doubles title (Wimbledon 2017)",
      "Halbfinale Wimbledon im Einzel (2018)": "Wimbledon singles semifinal (2018)",
      "WTA Finals Halbfinale (2017)": "WTA Finals semifinal (2017)",
      "Top 9 der Weltrangliste (2018)": "Top 9 in the world ranking (2018)",
      "Grand-Slam-Halbfinale im Einzel (Wimbledon 2013)": "Grand Slam singles semifinal (Wimbledon 2013)",
      "Grand-Slam-Titel im Mixed-Doppel (US Open 2022)": "Grand Slam mixed doubles title (US Open 2022)",
      "WTA-Einzeltitel (Quebec 2012)": "WTA singles title (Quebec 2012)",
      "Platz 13 Weltrangliste (2013)": "World ranking No. 13 (2013)",
      "Olympische Silbermedaille 2016 in Rio mit der deutschen Nationalmannschaft":
        "Olympic silver medal in Rio 2016 with the German national team",
      "Deutscher Pokalsieger 2014/15 mit Bayer 04 Leverkusen": "German Cup winner 2014/15 with Bayer 04 Leverkusen",
      "Vize-Weltmeister bei der U17-EM 2007": "Runner-up at the U17 European Championship in 2007",
      "Mehr als 250 Bundesligaspiele": "More than 250 Bundesliga matches",
      "Über 20 Länderspiele für Deutschland": "Over 20 international caps for Germany",
      "Langjähriger Kapitän bei Bayer Leverkusen": "Long-time captain at Bayer Leverkusen",
      "Grand-Slam-Titel im Doppel (French Open 2015)": "Grand Slam doubles title (French Open 2015)",
      "Grand-Slam-Titel im Doppel (Australian Open 2015)": "Grand Slam doubles title (Australian Open 2015)",
      "Grand-Slam-Finale im Einzel (French Open 2015)": "Grand Slam singles final (French Open 2015)",
      "Fünffache Grand-Slam-Titelträgerin im Doppel": "Five-time Grand Slam doubles champion",
      "Platz 5 Weltrangliste (2015)": "World ranking No. 5 (2015)",
      "Zweifache olympische Goldmedaillengewinnerin (2006 und 2010)":
        "Two-time Olympic gold medal winner (2006 and 2010)",
      "Zwölffache Weltmeisterin": "Twelve-time world champion",
      "34 Weltcupsiege": "34 World Cup victories",
      "Dreifache Gesamtweltcupsiegerin (2007/08, 2008/09, 2009/10)":
        "Three-time overall World Cup winner (2007/08, 2008/09, 2009/10)",
      "Olympische Silber- und Bronzemedaille": "Olympic silver and bronze medals",
      "Zweifache WTA-Doppeltitelgewinnerin": "Two-time WTA doubles title winner",
      "Platz 66 Weltrangliste (2012)": "World ranking No. 66 (2012)",
      "Langjährige Nummer 1 von Luxemburg im Damentennis":
        "Long-time Luxembourg No. 1 in women's tennis",
      "Mehrfache Teilnahme an Grand-Slam-Turnieren im Einzel und Doppel":
        "Multiple appearances in Grand Slam tournaments in singles and doubles",
      "Zweifache olympische Silbermedaillengewinnerin (2006)": "Two-time Olympic silver medal winner (2006)",
      "Dreifache Weltmeisterin (insgesamt 9 WM-Medaillen)": "Three-time world champion (9 World Championship medals in total)",
      "Gesamtweltcup-Siegerin im Verfolgungsweltcup (2005/06)":
        "Overall winner of the pursuit World Cup (2005/06)",
      "10 Weltcupsiege im Biathlon": "10 World Cup wins in biathlon",
      "Sprintweltmeisterin 1999": "Sprint World Champion 1999",
      "Zweifache olympische Silbermedaillengewinnerin": "Two-time Olympic silver medal winner",
      "Weltmeisterin in der Staffel": "World champion in relay",
      "Mehrere WM- und Weltcup-Podestplätze": "Multiple World Championship and World Cup podium finishes",
      "Teil der erfolgreichen deutschen Biathlon-Staffel in den 1990er Jahren":
        "Part of the successful German biathlon relay team in the 1990s",
      "2009/2010 Platz 7 Slalomweltcup": "2009/2010 7th place in the Slalom World Cup",
      "Formel-1-Team 2006 bis 2019": "Formula 1 team from 2006 to 2019",
      "Sieg beim Italien-GP 2008": "Victory at the 2008 Italian Grand Prix",
      "Schwesterteam von Red Bull Racing mit Fokus auf Junioren- und Talente-Entwicklung":
        "Sister team of Red Bull Racing focused on junior and talent development",
      "Deutscher Meister 2011/12 mit Borussia Dortmund": "German champion 2011/12 with Borussia Dortmund",
      "Zweifacher Deutscher Meister 2011 und 2012": "Two-time German champion in 2011 and 2012",
      "DFB-Pokalsieger 2016/17 mit Borussia Dortmund": "DFB Cup winner 2016/17 with Borussia Dortmund",
      "Über 300 Bundesliga-Spiele": "Over 300 Bundesliga matches",
      "Trainer der englischen Nationalmannschaft von 2001 bis 2006":
        "Head coach of the England national team from 2001 to 2006",
      "Zweimal Viertelfinale bei Weltmeisterschaften mit England (2002 und 2006)":
        "Two World Cup quarterfinals with England (2002 and 2006)",
      "Trainer von Top-Klubs wie SS Lazio und Manchester City":
        "Coach of top clubs such as SS Lazio and Manchester City",
      "Serie-A-Meister 1999/2000 mit Lazio Rom": "Serie A champion 1999/2000 with Lazio",
      "Gewinner des Europapokals der Pokalsieger 1998/99 mit Lazio":
        "Winner of the UEFA Cup Winners' Cup 1998/99 with Lazio",
      "Erfolgreichster deutscher Skisportler aller Zeiten": "Most successful German ski athlete of all time",
      "Zweifacher Juniorenweltmeister": "Two-time Junior World Champion",
      "Gesamtweltcupfünfter 2002/2003": "Fifth place in the overall World Cup 2002/2003",
      "Gewinner Wimbledon 1988,1990": "Wimbledon winner 1988, 1990",
      "Gewinner US Open 1991,1992": "US Open winner 1991, 1992",
      "Gewinner Australian Open 1985,1987": "Australian Open winner 1985, 1987",
      "41 Einzeltitel": "41 singles titles",
      "Stefan Edberg Tennis Academy im Cascade Resort, Lagos, Algarve, Portugal":
        "Stefan Edberg Tennis Academy at Cascade Resort, Lagos, Algarve, Portugal",
      "Fokus auf Nachwuchsförderung im Tennis für Kinder und Jugendliche":
        "Focus on youth development in tennis for children and teenagers",
      "Verbindung von Techniktraining, Athletik und mentaler Entwicklung":
        "Combination of technical training, athletic development, and mental development",
      "Zusammenarbeit mit lokalen Tennisvereinen und nationalen Verbänden":
        "Collaboration with local tennis clubs and national associations",
      "Vizeeuropameisterin 2025 Martell": "European Vice Champion 2025 Martell",
      "Juniorenweltmeisterin Staffel 2025 Östersund": "Junior World Champion relay 2025 Östersund",
      "Zweifache Silbermedaillengewinnerin JWM 2025 Östersund":
        "Two-time silver medal winner at Junior World Championships 2025 Östersund",
      "Silbermedaille Juniorenweltmeisterschaft 2026 Team in Lillehammer":
        "Silver medal Junior World Championships 2026 team in Lillehammer",
    },
    interests: {
      "Kunst": "Art",
      "Design": "Design",
      "Fotografie": "Photography",
      "In Zukunft noch hoffentlich viele Sportliche Erfolge feiern zu dürfen": "Hopefully celebrating many athletic successes in the future",
      "Rennrad fahren": "Road cycling",
      "verschiedene Sportarten (Bsp. Tour de france, Leichtathletik...)": "Various sports (e.g. Tour de France, athletics...)",
      "Mode": "Fashion",
      "Geschichte": "History",
      "Handarbeit": "Handicrafts",
      "Lesen": "Reading",
      "sehr vielseitig von Politik bis hin zu fantasy Büchern kann man mich für vieles begeistern": "Very versatile, from politics to fantasy books, I can be enthusiastic about many things",
      "alle anderen Sportarten": "All other sports",
      "Alle Sportarten": "All sports",
      "Musik": "Music",
      "Sport": "Sports",
      "Natur": "Nature",
      "Technik": "Technology",
      "Imkerei": "Beekeeping",
      "Garten und Natur": "Garden and nature",
      "Wintersport": "Winter sports",
      "Wintersport allgemein": "Winter sports in general",
      "Radsport": "Cycling",
      "Ernährung": "Nutrition",
      "Körperoptimierung": "Body optimization",
      "Fußball": "Football",
      "Tennis": "Tennis",
      "Eishockey": "Ice hockey",
      "Handwerk": "Crafts",
      "Landwirtschaft": "Agriculture",
      "Wandern": "Hiking",
      "Unterstützung des Projekts 'Eric-Frenzel -Trails'": "Support of the 'Eric-Frenzel -Trails' project",
      "Organisieren/plann": "Organizing/planning",
      "Sport allgemein": "Sports in general",
      "Psychologie": "Psychology",
      "mein Hund": "my dog",
      "Alles was generell mit Sport zu tun hat. Seit neuestem American Football": "Everything related to sports in general. Recently American Football",
      "Pflanzen": "Plants",
      "Reisen": "Travel",
      "Geografie": "Geography",
      "Französisch": "French",
      "Fashion": "Fashion",
      "Fußball spielen": "Playing football",
      "Sportliche Aktivitäten": "Athletic activities",
      "Radfahren": "Cycling",
      "Zeit mit Freunden verbringen": "Spending time with friends",
      "Alles, was mit Sport zu tun hat": "Everything related to sports",
      "Klavier spielen": "Playing piano",
      "kochen": "Cooking",
      "Kochen": "Cooking",
      "guten Kaffee trinken": "Drinking good coffee",
      "kreativ sein": "Being creative",
      "Golf": "Golf",
      "Kitesurfen": "Kitesurfing",
      "Fotoalben gestalten": "Creating photo albums",
      "backen": "Baking",
      "Biathlon": "Biathlon",
      "Kaffee/Barista": "Coffee/Barista",
      "Handball": "Handball",
      "Ausgleichssportarten": "Recreational sports",
      "Sport und Natur": "Sports and nature",
    },
    hobbies: {
      "Ski fahren, Mountain Biking, Laufen, Surfen, Malen, Gitarre spielen, Imkerei": "Skiing, mountain biking, running, surfing, painting, playing guitar, beekeeping",
      "Zeit mit Familie und Freunden verbringen": "Spending time with family and friends",
      "Zeit mit Familie und Freunde verbringen, Fernstudium in Sportökonomie, Biografien lesen, Konzerte besuchen,Backen ": "Spending time with family and friends, distance learning in sports economics, reading biographies, attending concerts, baking",
      "Reisen": "Traveling",
      "Bergtouren gehen und generell draußen Sport machen, natürlich geb ich mein Geld auch gerne für Klamotten und andere schöne Dinge aus und unternehme gerne was mit meinen Freunden und wenn die Zeit es auch erlaubt mit meiner Familie in meiner Heimat. ": "Going on mountain tours and generally doing sports outdoors, of course I also like to spend my money on clothes and other nice things and like to do things with my friends and when time allows with my family in my hometown.",
      "Skifahren, Radfahren, Gitarre spielen ": "Skiing, cycling, playing guitar",
      "Lesen, häkeln": "Reading, crocheting",
      "Zeit mit der Familie, Kochen, Wandern, Basteln": "Time with family, cooking, hiking, crafting",
      "Zeit mit der Familie und Freunden verbringen, Rennrad fahren": "Spending time with family and friends, road cycling",
      "Tennis, Bergtouren, Rennrad fahren, Padel": "Tennis, mountain tours, road cycling, padel",
      "Tennis, Fußball, Golf": "Tennis, football, golf",
      "Radeln, kochen": "Cycling, cooking",
      "Unternehmungen aller Art mit Freunden und Familie": "All kinds of activities with friends and family",
      "Lesen, Skifahren, Radfahren": "Reading, skiing, cycling",
      "Radfahren, Wandern": "Cycling, hiking",
      "Wandern, Fischen, Jagen": "Hiking, fishing, hunting",
      "Fußball, Radfahren, Zeit mit Familie und Freunden": "Football, cycling, spending time with family and friends",
      "Wandern, Bergsteigen": "Hiking, mountaineering",
      "Wandern, Langlaufen": "Hiking, cross-country skiing",
      "Skifahren, Skibergsteigen, Wandern, Backen/Kochen": "Alpine skiing, ski mountaineering, hiking, baking/cooking",
      "Alpine skiing, ski mountaineering, hiking, baking/cooking": "Alpine skiing, ski mountaineering, hiking, baking/cooking",
      "Wandern, Mountainbiking, Ökologischer Landbau, Kochen": "Hiking, mountain biking, organic farming, cooking",
      "Skifahren, Wandern, mit der Familie reisen, Pate des Allgäuer Bergwalds":
        "Skiing, hiking, traveling with family, patron of the Allgäu mountain forest",
      "Skifahren, Wandern, Mountainbiking, mit der Familie reisen und Unternehmungen machen":
        "Skiing, hiking, mountain biking, traveling with family and doing activities together",
      "Ich bin gerne draußen in der Natur. Sport Allgemein.": "I like being outdoors in nature. Sports in general.",
      "Schwimmen, Volleyball": "Swimming, volleyball",
      "Kaffee trinken mit Freunden und Familie, Squash, an die frische Luft gehen": "Drinking coffee with friends and family, squash, going outside",
      "Skitour/fahren, SUP, Gravelbiken, Berge, Go Kart, Kochen, Barista": "Ski touring/skiing, SUP, gravel biking, mountains, go-karting, cooking, barista",
      "Naturerleben, Reisen, Ausgleichssportarten, Wandern, Nordic Walking, Trailrunning": "Experiencing nature, traveling, recreational sports, hiking, Nordic walking, trail running",
      "Andere Sportarten wie Fußball, Radfahren, Golf oder Skialpin, Fotografie, mit Freunden treffen": "Other sports like football, cycling, golf or alpine skiing, photography, meeting friends",
      "Bergtouren, Essengehen, Freunde treffen, Skitour gehen": "Mountain tours, dining out, meeting friends, ski touring",
      "Lesen, Wandern, Volleyball spielen": "Reading, hiking, playing volleyball",
      "Lesen, Reisen, Schwimmen": "Reading, traveling, swimming",
      "Sportliche Aktivitäten, Fußball spielen, Radfahren sowie Zeit mit Freunden verbringen": "Athletic activities, playing football, cycling as well as spending time with friends",
      "Radfahren, Berggehen, Tauchen und vieles mehr": "Cycling, mountain hiking, diving and much more",
      "Ich fahre Rennrad und ich liebe es außerdem Motorrad zu fahren ": "I ride road bikes and I also love riding motorcycles",
      "Lesen, Zeit mit Familie, Klettern, Paddeltennis, Escaperooms":
        "Reading, spending time with family, climbing, paddle tennis, escape rooms",
      "Die Familie ist in meinem Leben sehr wichtig, deshalb versuche ich, so viel Zeit wie möglich mit ihr zu verbringen. Wenn ich auf Reisen bin, versuche ich immer, Sehenswürdigkeiten zu besuchen, um interessante Dinge oder Orte zu entdecken.": "Family is very important in my life, so I try to spend as much time as possible with them. When I'm traveling, I always try to visit sights to discover interesting things or places.",
      "In meiner Freizeit unternehme ich gerne was mit Freunden und backe und koche gerne mal.": "In my free time I like to do things with friends and like to bake and cook sometimes.",
      "Klettern, Snowboardfahren, Zeit draußen mit Freunden verbringen": "Climbing, snowboarding, spending time outdoors with friends",
      "DJ(auflegen), Skifahren, Lesen, Volleyball": "DJ (DJing), skiing, reading, volleyball",
      "Wandern, Mountain-Biking, Unternehmungen mit Freunden":
        "Hiking, mountain biking, activities with friends",
      "Kochen und Backen, Spaziergänge, Skifahren, Fahrrad fahren":
        "Cooking and baking, walks, skiing, cycling",
      "Wandern, Gleitschirmfliegen, in der Natur sein und mit ihr leben":
        "Hiking, paragliding, being in nature and living with it",
    },
  },
  fr: {
    sportDisciplines: {
      Skispringen: "Saut à ski",
      Biathlon: "Biathlon",
      "Nordische Kombination": "Combiné nordique",
      "Ski Alpin": "Ski alpin",
      Spezialspringen: "Saut spécial",
      "Track and Field": "Athlétisme",
      Tennis: "Tennis",
      Fußball: "Football",
      "Formel 1": "Formule 1",
      DTM: "DTM",
      Langlauf: "Ski de fond",
      Skilanglauf: "Ski de fond",
    },
    professions: {
      Skispringerin: "Sauteuse à ski",
      Skispringer: "Sauteur à ski",
      Biathletin: "Biathlète",
      "Nordische Kombiniererin": "Athlète du combiné nordique",
      "Nordischer Kombinierer": "Athlète du combiné nordique",
      Skirennläuferin: "Skieuse alpine",
      Sportsoldatin: "Militaire de carrière (sport)",
      Sportsoldat: "Militaire de carrière (sport)",
      Bundespolizistin: "Policière fédérale",
      Polizeivollzugsbeamter: "Agent de police",
      Polizist: "Policier",
      Polizeimeisterin: "Brigadière",
      Zollbeamter: "Douanier",
      Schülerin: "Étudiante",
      "Schülerin/Studentin": "Étudiante",
      Berufssoldatin: "Militaire de carrière",
      "Bundestrainer Nordische Kombination": "Entraîneur national combiné nordique",
      Bundeswehrangestellte: "Employée des forces armées",
      Tennisspielerin: "Joueuse de tennis",
      Fußballer: "Footballeur",
      Fußballtrainer: "Entraîneur de football",
      Rennstall: "Écurie de course",
      Tennisakademie: "Académie de tennis",
      Sportler: "Sportif / sportive",
      " Student für Recht und Wirtschaft": " Étudiante en droit et économie",
      Rennfahrer: "Pilote automobile",
      Langläuferin: "Fondeuse",
    },
    achievements: {},
    interests: {},
    hobbies: {},
  },
  es: {
    sportDisciplines: {
      Skispringen: "Salto en esquí",
      Biathlon: "Biatlón",
      "Nordische Kombination": "Combinado nórdico",
      "Ski Alpin": "Esquí alpino",
      Spezialspringen: "Salto especial",
      "Track and Field": "Atletismo",
      Tennis: "Tenis",
      Fußball: "Fútbol",
      "Formel 1": "Fórmula 1",
      DTM: "DTM",
      Langlauf: "Esquí de fondo",
      Skilanglauf: "Esquí de fondo",
    },
    professions: {
      Skispringerin: "Saltadora de esquí",
      Skispringer: "Saltador de esquí",
      Biathletin: "Biatleta",
      "Nordische Kombiniererin": "Deportista de combinado nórdico",
      "Nordischer Kombinierer": "Deportista de combinado nórdico",
      Skirennläuferin: "Esquiadora alpina",
      Sportsoldatin: "Militar profesional (deporte)",
      Sportsoldat: "Militar profesional (deporte)",
      Bundespolizistin: "Policía federal",
      Polizeivollzugsbeamter: "Agente de policía",
      Polizist: "Policía",
      Polizeimeisterin: "Oficial de policía",
      Zollbeamter: "Aduanero",
      Schülerin: "Estudiante",
      "Schülerin/Studentin": "Estudiante",
      Berufssoldatin: "Militar profesional",
      "Bundestrainer Nordische Kombination": "Seleccionador nacional de combinado nórdico",
      Bundeswehrangestellte: "Empleada de las fuerzas armadas",
      Tennisspielerin: "Tenista",
      Fußballer: "Futbolista",
      Fußballtrainer: "Entrenador de fútbol",
      Rennstall: "Escudería",
      Tennisakademie: "Academia de tenis",
      Sportler: "Deportista",
      " Student für Recht und Wirtschaft": " Estudiante de derecho y economía",
      Rennfahrer: "Piloto",
      Langläuferin: "Esquiadora de fondo",
    },
    achievements: {},
    interests: {},
    hobbies: {},
  },
};

// Ensure non-DE athlete text categories are fully populated.
// FR/ES keep their own entries, and automatically inherit EN as fallback base.
const ATHLETE_TEXT_CATEGORIES = ["achievements", "interests", "hobbies"];
["fr", "es"].forEach((localeKey) => {
  ATHLETE_TEXT_CATEGORIES.forEach((categoryKey) => {
    translations[localeKey][categoryKey] = {
      ...(translations.en[categoryKey] || {}),
      ...(translations[localeKey][categoryKey] || {}),
    };
  });
});

const SUPPORTED_ATHLETE_LANGS = ["de", "en", "fr", "es"];

// Function to get athletes in the specified language
export const getAthletes = (language = "de") => {
  const lang = SUPPORTED_ATHLETE_LANGS.includes(language) ? language : "de";
  const langTranslations = translations[lang] || translations.en;

  const pick = (category, key) => {
    if (!key) return key;
    // German is our source dataset language for most free-text fields.
    // Do not fall back to English when locale is "de".
    if (lang === "de") {
      return langTranslations[category]?.[key] ?? key;
    }

    // For non-DE locales: prefer locale-specific mapping, then English, then raw source text.
    return langTranslations[category]?.[key] ?? translations.en[category]?.[key] ?? key;
  };

  const mappedAthletes = athletes.map((athlete) => {
    const localPortrait = getLocalAthleteImage(athlete.firstName, athlete.lastName, "portrait");
    const localAction = getLocalAthleteImage(athlete.firstName, athlete.lastName, "action");
    const portraitImageURL = localPortrait.src || athleteImagePlaceholder;
    const actionImageURL = localAction.src || athleteImagePlaceholder;
    const fullName = `${athlete.firstName} ${athlete.lastName}`;
    const photoCredits = {
      ...(athlete.photoCredits || {}),
      ...(photoCreditsByAthlete[fullName] || {}),
    };

    const translatedAchievements = (athlete.achievements || []).map((achievement) => pick("achievements", achievement));

    const translatedInterests = (athlete.interests || []).map((interest) => pick("interests", interest));

    const translatedHobby = athlete.hobby ? pick("hobbies", athlete.hobby) : "";

    let translatedProfession = athlete.profession || "";
    if (athlete.profession) {
      const mapped = pick("professions", athlete.profession);
      if (mapped !== athlete.profession) {
        translatedProfession = mapped;
      } else {
        const professionParts = athlete.profession.split(", ");
        if (professionParts.length > 1) {
          const firstPart = professionParts[0];
          const rest = professionParts.slice(1).join(", ");
          const firstMapped = pick("professions", firstPart);
          if (firstMapped !== firstPart) {
            translatedProfession = `${firstMapped}, ${rest}`;
          } else {
            translatedProfession = athlete.profession;
          }
        } else {
          translatedProfession = athlete.profession;
        }
      }
    }

    const disc = athlete.sportDiscipline ? pick("sportDisciplines", athlete.sportDiscipline) : athlete.sportDiscipline;

    const { borrowedActionImageFrom, ...athleteWithoutBorrow } = athlete;

    return {
      ...athleteWithoutBorrow,
      portraitImageURL,
      actionImageURL,
      borrowedActionImageFrom,
      photoCredits,
      sportDiscipline: disc || athlete.sportDiscipline,
      profession: translatedProfession,
      achievements: translatedAchievements,
      interests: translatedInterests,
      hobby: translatedHobby,
    };
  });

  return mappedAthletes.map((athlete) => {
    const borrow = athlete.borrowedActionImageFrom;
    if (!borrow) {
      const { borrowedActionImageFrom: _unused, ...rest } = athlete;
      return rest;
    }

    const sourceAthlete = mappedAthletes.find(
      (candidate) => candidate.firstName === borrow.firstName && candidate.lastName === borrow.lastName
    );
    const { borrowedActionImageFrom: _unused, ...rest } = athlete;

    return {
      ...rest,
      actionImageURL: sourceAthlete?.actionImageURL || athleteImagePlaceholder,
    };
  });
};

// Default export for backward compatibility
export default athletes;
